import { getAllTagsFn } from "@/actions/tags/handler";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { seo } from "@/utils/seo";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { Edit, Eye, MessageSquare } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/forum/")({
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery({
      queryKey: ["tags"],
      queryFn: getAllTagsFn,
    });
  },
  head: () => ({
    meta: seo({
      title: "Forum | theCoach",
      description:
        "Powerlifting, fitness, vücut geliştirme ve antrenman üzerine toplulukla etkileşime geçin. Soru sorun, bilgi paylaşın ve spor dünyasına katılın.",
      keywords:
        "powerlifting, fitness, vücut geliştirme, spor forumu, antrenman, beslenme, supplement, sporcu sağlığı",
      canonical: "https://atalaythecoach.com/forum",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Forum | theCoach",
          description: "Sporcular için tartışma ve bilgi paylaşım platformu.",
          url: "https://atalaythecoach.com/forum",
          publisher: {
            "@type": "Organization",
            name: "theCoach",
            logo: {
              "@type": "ImageObject",
              url: "https://atalaythecoach.com/favicon/favicon.svg",
            },
          },
        }),
      },
    ],
  }),
  component: ForumIndexPage,
});

function ForumIndexPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: getAllTagsFn,
  });
  const { user } = useRouteContext({ from: "/_app" });
  const navigate = useNavigate();
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const handleCreateTopicClick = () => {
    if (user) {
      navigate({ to: "/forum/create" });
    } else {
      setShowLoginPopup(true);
    }
  };

  return (
    <>
      <div className="min-h-screen w-full bg-background pt-28 pb-16 text-foreground sm:pt-32">
        <div className="container mx-auto max-w-7xl px-4">
          <header className="mb-8 flex flex-col items-start justify-between sm:mb-12 sm:flex-row sm:items-center">
            <div>
              <h1 className="font-extrabold font-serif text-4xl tracking-tight sm:text-5xl">
                Forum
              </h1>
              <p className="mt-2 text-lg text-muted-foreground">
                Toplulukla etkileşime geçin, sorular sorun ve bilgi paylaşın.
              </p>
            </div>
            <Button onClick={handleCreateTopicClick}>
              <Edit className="mr-2 h-4 w-4" />
              Yeni Konu Oluştur
            </Button>
          </header>

          <div className="hidden overflow-hidden rounded-lg border md:block">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[60%]">Konu</TableHead>
                  <TableHead className="text-center">İstatistikler</TableHead>
                  <TableHead className="text-left">Son Mesaj</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? // Skeleton rows for desktop table
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell className="py-4">
                          <div className="flex items-center space-x-4">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-[250px]" />
                              <Skeleton className="h-3 w-[150px]" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-4">
                            <Skeleton className="h-4 w-12" />
                            <Skeleton className="h-4 w-12" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  : data?.body?.tags.map((topic) => (
                      <TableRow key={topic.tagId}>
                        <TableCell className="py-4">
                          <div className="flex items-center space-x-4">
                            <Avatar>
                              <AvatarImage
                                src={topic.creater?.image || ""}
                                alt={topic.creater?.userName || "Kullanıcı"}
                              />
                              <AvatarFallback>
                                {topic.creater?.userName[0].toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <Link
                                to="/forum/$tagId"
                                params={{ tagId: topic.tagId }}
                                className="font-semibold text-foreground transition-colors hover:text-primary"
                              >
                                {topic.tagTitle}
                              </Link>
                              <p className="mt-1 text-muted-foreground text-xs">
                                {topic.creater?.userName} · {topic.category}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          <div className="flex items-center justify-center gap-4">
                            <div className="flex items-center gap-1.5">
                              <MessageSquare className="h-4 w-4 text-muted-foreground" />{" "}
                              {topic?.messageCount || 0}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Eye className="h-4 w-4 text-muted-foreground" />{" "}
                              {/* {topic?.views || 0}{" "} */}
                              {0}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-left text-sm">
                          {`${new Date(topic.createdAt).toLocaleDateString()} ${new Date(topic.createdAt).toLocaleTimeString()}`}
                          <p className="text-muted-foreground">
                            by {topic.creater?.userName}
                          </p>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-5 space-y-4 md:hidden ">
            {isLoading
              ? // Skeleton cards for mobile view
                Array.from({ length: 5 }).map((_) => (
                  <Card>
                    <CardContent className="flex items-start space-x-4 p-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="grow space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                        <div className="mt-2 flex items-center gap-4">
                          <Skeleton className="h-3 w-16" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              : data?.body.tags.map((topic) => (
                  <Link
                    key={topic.tagId}
                    to="/forum/$tagId"
                    params={{ tagId: topic.tagId }}
                  >
                    <Card className="transition-colors hover:border-primary">
                      <CardContent className="flex items-start space-x-4 p-4">
                        <Avatar>
                          <AvatarImage
                            src={topic.creater?.image || ""}
                            alt={topic.creater?.userName || "Kullanıcı"}
                          />
                          <AvatarFallback>
                            {topic.creater?.userName[0].toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grow">
                          <h3 className="font-semibold leading-snug">
                            {topic.tagTitle}
                          </h3>
                          <p className="mt-1 text-muted-foreground text-xs">
                            {topic.creater?.userName} · {topic.category}
                          </p>
                          <div className="mt-2 flex items-center gap-4 text-muted-foreground text-xs">
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />{" "}
                              {topic?.messageCount || 0} Mesaj
                            </div>
                            <div className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />{" "}
                              {/* {topic?.views || 0}{" "} */}0 Görüntüleme
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
          </div>
        </div>
      </div>

      <AlertDialog open={showLoginPopup} onOpenChange={setShowLoginPopup}>
        <AlertDialogContent className="text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Giriş Gerekli</AlertDialogTitle>
            <AlertDialogDescription>
              Yeni bir konu oluşturabilmek için lütfen hesabınıza giriş yapın.
              Hesabınız yoksa kolayca oluşturabilirsiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                asChild
                variant="default"
                onClick={() => setShowLoginPopup(false)}
              >
                <Link to="/password/login">Giriş Yap</Link>
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
